import { useQuery } from '@tanstack/react-query';
import { EMPTY_PENDING_COUNTS, type FinancePendingCountsDto } from '@hrms/shared';
import { get } from '@/lib/api-client';

/**
 * Which route each queue's count belongs beside.
 *
 * Mapped here rather than returned by the API: the server should not have to
 * know the front end's URLs, and a renamed route is then a one-line change on
 * the side that owns routing.
 */
const ROUTE_OF: Record<keyof FinancePendingCountsDto, string> = {
  fundApprovals: '/finance/approvals',
  fundReleases: '/finance/release',
  liquidationsToVerify: '/finance/liquidations',
  expenseClaimApprovals: '/finance/expense-claims/approvals',
};

/**
 * Counts of work waiting, keyed by the route that clears it.
 *
 * Polled rather than pushed: the number only has to be roughly current, and a
 * minute-stale badge is a far smaller cost than a socket per signed-in phone.
 * Failures resolve to zeroes — a sidebar is not the place to report an outage,
 * and a missing badge is better than a broken nav.
 */
export function usePendingBadges(): Record<string, number> {
  const { data = EMPTY_PENDING_COUNTS } = useQuery({
    queryKey: ['finance', 'pending-counts'],
    queryFn: () => get<FinancePendingCountsDto>('/finance/pending-counts'),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
    // Nothing here is worth a red screen; an absent badge reads as "nothing
    // waiting", which is also what a user with no queues should see.
    throwOnError: false,
  });

  const badges: Record<string, number> = {};
  for (const [key, route] of Object.entries(ROUTE_OF)) {
    const count = data[key as keyof FinancePendingCountsDto];
    if (count > 0) badges[route] = count;
  }

  return badges;
}
